import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RotateCcw, Activity } from "lucide-react";

function getInterpretation(map) {
  if (map >= 70 && map <= 105) return { label: "Normal", color: "bg-green-500", textColor: "text-green-500", bgColor: "bg-green-500/10 border-green-500/30" };
  if (map >= 60 && map < 70) return { label: "Low", color: "bg-amber-500", textColor: "text-amber-500", bgColor: "bg-amber-500/10 border-amber-500/30" };
  if (map < 60) return { label: "Critically Low", color: "bg-red-500", textColor: "text-red-500", bgColor: "bg-red-500/10 border-red-500/30" };
  return { label: "Elevated", color: "bg-amber-500", textColor: "text-amber-500", bgColor: "bg-amber-500/10 border-amber-500/30" };
}

export default function MAPCalculator() {
  const [sbp, setSbp] = useState("");
  const [dbp, setDbp] = useState("");
  const [open, setOpen] = useState(false);

  const hasBoth = sbp && dbp && parseFloat(sbp) > 0 && parseFloat(dbp) > 0;
  const map = hasBoth ? ((parseFloat(sbp) + 2 * parseFloat(dbp)) / 3).toFixed(0) : null;
  const interp = map ? getInterpretation(parseFloat(map)) : null;

  const handleReset = () => { setSbp(""); setDbp(""); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="map-trigger">
          <Activity className="w-3.5 h-3.5" />
          MAP
          {map && <Badge className={`${interp.color} text-white text-[10px] ml-1`}>{map}</Badge>}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Activity className="w-5 h-5" /> Mean Arterial Pressure
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground">MAP = (SBP + 2 × DBP) ÷ 3</p>

        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">SBP (mmHg)</label>
              <Input
                type="number"
                placeholder="120"
                value={sbp}
                onChange={(e) => setSbp(e.target.value)}
                className="font-mono"
                data-testid="map-sbp-input"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">DBP (mmHg)</label>
              <Input
                type="number"
                placeholder="80"
                value={dbp}
                onChange={(e) => setDbp(e.target.value)}
                className="font-mono"
                data-testid="map-dbp-input"
              />
            </div>
          </div>

          {/* Result */}
          <div className={`rounded-lg border p-4 ${map ? interp.bgColor : 'bg-muted/30'}`} data-testid="map-result">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-medium uppercase tracking-wide">MAP</span>
                <div className="flex items-baseline gap-1.5">
                  <span className={`text-3xl font-bold font-mono ${map ? interp.textColor : 'text-muted-foreground'}`} data-testid="map-value">
                    {map || "--"}
                  </span>
                  <span className="text-sm text-muted-foreground">mmHg</span>
                </div>
              </div>
              <div className="text-right flex flex-col items-end gap-1">
                {map && <Badge className={`${interp.color} text-white`}>{interp.label}</Badge>}
                <Button variant="ghost" size="sm" onClick={handleReset} className="h-6 text-xs px-2" data-testid="map-reset">
                  <RotateCcw className="w-3 h-3 mr-1" /> Reset
                </Button>
              </div>
            </div>
          </div>

          {/* Reference */}
          <div className="text-xs text-muted-foreground space-y-1 border-t pt-3">
            <p className="font-medium">Reference Ranges:</p>
            <div className="grid grid-cols-2 gap-1">
              <span>Normal:</span><span className="text-green-500 font-mono">70–105 mmHg</span>
              <span>Low:</span><span className="text-amber-500 font-mono">60–69 mmHg</span>
              <span>Critical:</span><span className="text-red-500 font-mono">&lt; 60 mmHg</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
